
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import './graduateprofile.css';
import { getStaticTalentBySlug } from '../data/talent-data';
import { fetchGraduateBySlug } from '../utils/graduatesApi';
import { mapApiGraduate, mapStaticTalent } from '../utils/talentHelpers';
import avatar from '../assets/images/avatar.webp';

const TalentPhoto = ({ talent, className = '' }) => {
    const [imgSrc, setImgSrc] = useState(talent.image);

    return (
        <img
            src={imgSrc}
            alt={`${talent.name} - ${talent.title}`}
            className={className}
            loading="lazy"
            onError={() => setImgSrc(talent.fallbackImage || avatar)}
        />
    );
};

const GraduateProfile = () => {
    const { slug } = useParams();
    const [graduate, setGraduate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setNotFound(false);

            const staticTalent = getStaticTalentBySlug(slug);
            if (staticTalent) {
                if (!cancelled) {
                    setGraduate(mapStaticTalent(staticTalent));
                    setIsLoading(false);
                }
                return;
            }

            const apiData = await fetchGraduateBySlug(slug);
            if (cancelled) return;

            if (apiData) {
                setGraduate(mapApiGraduate(apiData));
            } else {
                setNotFound(true);
            }
            setIsLoading(false);
        };

        load();
        return () => { cancelled = true; };
    }, [slug]);

    const firstName = graduate?.name?.split(' ')[0] || 'this graduate';

    if (isLoading) {
        return (
            <div className="graduate-profile-page">
                <div className="gp-loading">
                    <div className="gp-spinner" role="status" aria-label="Loading" />
                    <p>Loading graduate profile...</p>
                </div>
            </div>
        );
    }

    if (notFound || !graduate) {
        return (
            <div className="graduate-profile-page">
                <div className="gp-empty">
                    <div className="gp-empty-icon"><i className="fas fa-user-slash"></i></div>
                    <h2>Graduate not found</h2>
                    <p>This profile may be unpublished or the link is incorrect.</p>
                    <Link to="/graduates" className="gp-btn-primary">
                        Browse all graduates <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="graduate-profile-page">
            <div className="gp-banner">
                <div className="gp-banner-pattern" aria-hidden="true" />
                <div className="container gp-banner-inner">
                    <Link to="/graduates" className="gp-back-link">
                        <i className="fas fa-arrow-left"></i> Talent marketplace
                    </Link>
                </div>
            </div>

            <div className="container gp-shell">
                <header className="gp-header-card">
                    <div className="gp-photo-col">
                        <div className="gp-photo-frame">
                            <TalentPhoto talent={graduate} className="gp-photo" />
                            {graduate.verified && (
                                <span className="gp-photo-verified" title="Verified Graduate">
                                    <i className="fas fa-check"></i>
                                </span>
                            )}
                        </div>
                        {graduate.availability === 'Available' && (
                            <span className="gp-availability-pill">
                                <span className="gp-availability-dot" />
                                Open to opportunities
                            </span>
                        )}
                    </div>

                    <div className="gp-identity">
                        <div className="gp-badge-row">
                            {graduate.verified && (
                                <span className="gp-badge gp-badge-verified">
                                    <i className="fas fa-shield-alt"></i> Verified Graduate
                                </span>
                            )}
                            {graduate.source === 'api' && (
                                <span className="gp-badge gp-badge-published">
                                    <i className="fas fa-globe-africa"></i> ATH Academy
                                </span>
                            )}
                        </div>

                        <h1 className="gp-name">{graduate.name}</h1>
                        <p className="gp-headline">{graduate.title}</p>
                        {graduate.programme && graduate.programme !== graduate.title && (
                            <p className="gp-programme">
                                <i className="fas fa-book-open"></i> {graduate.programme}
                            </p>
                        )}

                        <div className="gp-stat-chips">
                            <span className="gp-stat-chip">
                                <i className="fas fa-map-marker-alt"></i>
                                {graduate.location}
                            </span>
                            <span className="gp-stat-chip">
                                <i className="fas fa-graduation-cap"></i>
                                {graduate.experience}
                            </span>
                            {graduate.graduatedAt && (
                                <span className="gp-stat-chip">
                                    <i className="fas fa-calendar-check"></i>
                                    {graduate.graduatedAt}
                                </span>
                            )}
                        </div>
                    </div>
                </header>

                <div className="gp-body">
                    <main className="gp-main">
                        <section className="gp-panel">
                            <h2 className="gp-panel-title">
                                <i className="fas fa-user"></i> About
                            </h2>
                            <p className="gp-bio">{graduate.description}</p>
                        </section>

                        {graduate.skills?.length > 0 && (
                            <section className="gp-panel">
                                <h2 className="gp-panel-title">
                                    <i className="fas fa-code"></i> Skills &amp; expertise
                                </h2>
                                <div className="gp-skills-grid">
                                    {graduate.skills.map((skill) => (
                                        <span key={skill} className="gp-skill">{skill}</span>
                                    ))}
                                </div>
                            </section>
                        )}
                    </main>

                    <aside className="gp-sidebar">
                        <div className="gp-sidebar-card gp-sidebar-cta">
                            <div className="gp-sidebar-icon">
                                <i className="fas fa-handshake"></i>
                            </div>
                            <h3>Interested in hiring?</h3>
                            <p>
                                {firstName} is a verified ATH Academy graduate, ready to contribute to your team.
                            </p>
                            <Link
                                to={`/company-register?talent_slug=${encodeURIComponent(graduate.slug)}&talent_name=${encodeURIComponent(graduate.name)}&talent_title=${encodeURIComponent(graduate.title || '')}`}
                                className="gp-btn-primary gp-btn-block"
                            >
                                Hire {firstName} <i className="fas fa-arrow-right"></i>
                            </Link>
                            <Link to="/graduates" className="gp-btn-ghost gp-btn-block">
                                Browse more talent
                            </Link>
                        </div>

                        <div className="gp-sidebar-card">
                            <h3 className="gp-sidebar-title">Profile highlights</h3>
                            <ul className="gp-highlights">
                                <li>
                                    <span className="gp-highlight-icon"><i className="fas fa-check-circle"></i></span>
                                    <div>
                                        <strong>Verified training</strong>
                                        <span>Completed ATH Academy programme</span>
                                    </div>
                                </li>
                                <li>
                                    <span className="gp-highlight-icon"><i className="fas fa-laptop-code"></i></span>
                                    <div>
                                        <strong>{graduate.title}</strong>
                                        <span>Primary specialisation</span>
                                    </div>
                                </li>
                                <li>
                                    <span className="gp-highlight-icon"><i className="fas fa-map-pin"></i></span>
                                    <div>
                                        <strong>{graduate.location}</strong>
                                        <span>Based in Nigeria</span>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {graduate.githubUrl && (
                            <div className="gp-sidebar-card">
                                <h3 className="gp-sidebar-title">Portfolio</h3>
                                <a
                                    href={graduate.githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="gp-github-link"
                                >
                                    <i className="fab fa-github"></i>
                                    <span>
                                        <strong>GitHub profile</strong>
                                        <small>View projects &amp; code</small>
                                    </span>
                                    <i className="fas fa-external-link-alt"></i>
                                </a>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default GraduateProfile;
